import { Component, Input, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { StateService } from '@uirouter/angular';
import * as _ from 'lodash';

import { NotificationService } from '../../../../core/services/core.notification.service';
import { UploadService } from '../../../../core/services/core.upload.service';
import { TrackeritemService } from '../../services/trackeritem.service';
import { NotebookHost, NotebookUploadCallbacks } from '../../../../widgets/notebook/notebook-host';

/**
 * Tracker item Notebook tab. A thin host wrapper around <gf-notebook>: it builds a
 * {@link NotebookHost} describing the tracker-item specifics (item-level attachments,
 * edit-screen notifications) and lets the shared component own the rest.
 */
@Component({
  selector: 'ti-notebook',
  template: require('./trackeritem.notebook-tab.component.html')
})
export class NotebookTabComponent implements OnInit {
  @Input() trackeritem: any;
  @Input() acls: any;

  host!: NotebookHost;

  constructor(
    private notificationService: NotificationService,
    private http: HttpClient,
    private uploadService: UploadService,
    private trackeritemService: TrackeritemService,
    private stateService: StateService
  ) {}

  ngOnInit(): void {
    const self = this;
    this.host = {
      section: 'trackeritem',
      get refId() { return self.trackeritem.id; },
      get canUpdate() { return !!(self.acls && self.acls.canUpdate); },
      enableVisibility: false,
      enableWikiLinks: false,
      enablePublicLinks: false,
      attachmentsPerPage: false,

      pageListParams: () => ({ section: 'trackeritem', refId: self.trackeritem.id }),

      // Attachments live on the tracker item itself (shared across notebook pages).
      listAttachments: () => Promise.resolve(_.get(self.trackeritem, 'rel.attachments', [])),
      uploadAttachments: (_page: any, files: File[], callbacks: NotebookUploadCallbacks) =>
        self.uploadAttachments(files, callbacks),
      deleteAttachment: (_page: any, file: any) => self.deleteAttachment(file),

      onChange: (pages: any[]) => self.syncNotebookRel(pages),
      onVersionsRestored: () => self.refreshTimeline(),

      // Deep-linking: select the page named in the URL on load, and keep the URL in sync
      // (silently, via a squashed/dynamic 'pagename' param) as the selection changes.
      initialPagename: self.stateService.params.pagename || null,
      onPageSelected: (page: any) => self.stateService.go('.',
        { pagename: page && !page.isDraft ? page.pagename : null },
        { location: 'replace', inherit: true }),
    };
  }

  private uploadAttachments(files: File[], callbacks: NotebookUploadCallbacks): Promise<any> {
    const options = {
      successCallback: this.onAttachmentUploaded.bind(this),
      progressCallback: callbacks.progressCallback,
      finishedCallback: callbacks.finishedCallback
    };
    return this.uploadService.uploadFiles('trackeritem', this.trackeritem.id, files, options);
  }

  private onAttachmentUploaded(_section: string, _refId: number, file: any, hash: any): Promise<any> {
    const params = {
      fileName: file.name,
      fileType: file.type || 'application/octet-stream'
    };
    return this.trackeritemService.setAttachment(this.trackeritem.id, hash, params)
      .then((response: any) => {
        if (!_.has(response, 'location')) { return; }
        return this.http.get(response.location).toPromise()
          .then((attachment: any) => {
            if (!this.trackeritem.rel) { this.trackeritem.rel = {}; }
            if (!this.trackeritem.rel.attachments) { this.trackeritem.rel.attachments = []; }
            if (!_.some(this.trackeritem.rel.attachments, { id: attachment.id })) {
              this.trackeritem.rel.attachments.unshift(attachment);
            }
            this.notify();
          });
      });
  }

  private deleteAttachment(file: any): Promise<any> {
    return this.http.delete(`/api/trackeritem/${this.trackeritem.id}/attachment/${file.id}`)
      .toPromise()
      .then(() => {
        this.trackeritem.rel.attachments = this.trackeritem.rel.attachments.filter(
          (f: any) => f.id !== file.id
        );
        this.notify();
      });
  }

  private syncNotebookRel(pages: any[]): void {
    if (!this.trackeritem) { return; }
    if (!this.trackeritem.rel) { this.trackeritem.rel = {}; }
    this.trackeritem.rel.notebooks = pages;
    this.notify();
  }

  private refreshTimeline(): void {
    this.trackeritemService.fetchTimeline(this.trackeritem.id)
      .then((timelineData: any) => {
        if (!this.trackeritem.rel) { this.trackeritem.rel = {}; }
        ['history', 'messages', 'merge_comments', 'commits', 'associations', 'dependencies', 'duplicates', 'events'].forEach((key: string) => {
          if (_.has(timelineData, `rel.${key}`)) {
            _.set(this.trackeritem, `rel.${key}`, timelineData.rel[key]);
          }
        });
        this.notify();
      })
      .catch(() => {});
  }

  private notify(): void {
    this.notificationService.notifyAll('TRACKER_ITEM.EDIT.UPDATED', {
      trackeritem: this.trackeritem,
      preventCleanup: true
    });
  }
}
